import { useCallback, useEffect, useMemo, useState } from "react";
import StatusBadge from "../../intelligence/StatusBadge";
import {
  getAdminSourceQuality,
  getAdminSourceQualityDetail,
  recalculateAllSourceQuality,
  recalculateSourceQuality,
} from "../../../../services/midpointApi";
import { trustScoreTone } from "../../../../utils/sourceTrustLabels";
import SourceQualityDetailDrawer from "./SourceQualityDetailDrawer";

function confidenceTone(confidence) {
  if (confidence === "HIGH") {
    return "success";
  }

  if (confidence === "MEDIUM") {
    return "medium";
  }

  return "neutral";
}

const SourceQualityPage = ({ onBack, onOpenSourceControls, onOpenImageHealth }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getAdminSourceQuality();
      setSources(data.sources || []);
    } catch (err) {
      setError(err.message || "Unable to load source quality metrics.");
      setSources([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const summary = useMemo(() => {
    const withScores = sources.filter((s) => s.trustScore != null);
    const avg =
      withScores.length > 0
        ? Math.round(
            withScores.reduce((sum, s) => sum + s.trustScore, 0) / withScores.length
          )
        : null;

    const buckets = { Excellent: 0, Good: 0, Fair: 0, Weak: 0, Poor: 0, Unknown: 0 };
    sources.forEach((s) => {
      const label = s.trustLabel || "Unknown";
      buckets[label] = (buckets[label] || 0) + 1;
    });

    return {
      total: sources.length,
      average: avg,
      buckets,
      lowConfidence: sources.filter(
        (s) => s.trustConfidence === "LOW" || s.trustConfidence === "UNKNOWN"
      ).length,
      needsAttention: sources.filter((s) => (s.topIssues || []).length > 0).length,
    };
  }, [sources]);

  const openDetail = async (sourceKey) => {
    setSelectedKey(sourceKey);
    setDetailLoading(true);
    setDetail(null);

    try {
      const data = await getAdminSourceQualityDetail(sourceKey);
      const { success, ...detailPayload } = data;
      setDetail(detailPayload);
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRecalculateAll = async () => {
    try {
      setRecalculating(true);
      await recalculateAllSourceQuality();
      await loadList();
      setToast({ message: "Recalculated trust scores for all sources.", tone: "success" });
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    } finally {
      setRecalculating(false);
    }
  };

  const handleRecalculateOne = async (sourceKey) => {
    try {
      setRecalculating(true);
      const data = await recalculateSourceQuality(sourceKey);
      await loadList();
      if (selectedKey === sourceKey) {
        await openDetail(sourceKey);
      }
      const delta = data.trust_score_delta;
      const deltaText =
        delta != null && delta !== 0
          ? ` Trust changed from ${data.previous_trust_score ?? "—"} to ${data.trust_score ?? "—"}.`
          : "";
      setToast({
        message: data.success
          ? `Source trust recalculated.${deltaText}`
          : data.error || "Trust recalculation failed.",
        tone: data.success ? "success" : "error",
      });
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <div className="admin-page source-quality-page">
      <header className="admin-page__header">
        <div>
          <button type="button" className="admin-back-link" onClick={onBack}>
            ← Back to map
          </button>
          <p className="eyebrow">Administration · Real Estate</p>
          <h1>Source Quality</h1>
          <p className="admin-page__lede">
            Measurable trust scores from run reliability, freshness, completeness, images,
            image health, duplicate matching, review burden, and error severity. Formula version
            2026-05-error-severity-v1.
          </p>
        </div>
        <div className="admin-page__actions">
          {onOpenImageHealth ? (
            <button type="button" onClick={onOpenImageHealth}>
              Image health
            </button>
          ) : null}
          <button type="button" onClick={handleRecalculateAll} disabled={recalculating}>
            {recalculating ? "Recalculating…" : "Recalculate all"}
          </button>
        </div>
      </header>

      {toast ? (
        <p className={`admin-toast admin-toast--${toast.tone}`} role="status">
          {toast.message}
        </p>
      ) : null}

      <section className="source-quality-summary">
        <article className="panel-card">
          <p className="eyebrow">Sources</p>
          <strong className="source-quality-summary__value">{summary.total}</strong>
        </article>
        <article className="panel-card">
          <p className="eyebrow">Average trust</p>
          <strong className="source-quality-summary__value">
            {summary.average ?? "—"}
          </strong>
        </article>
        <article className="panel-card">
          <p className="eyebrow">Low confidence</p>
          <strong className="source-quality-summary__value">{summary.lowConfidence}</strong>
        </article>
        <article className="panel-card">
          <p className="eyebrow">Needs attention</p>
          <strong className="source-quality-summary__value">{summary.needsAttention}</strong>
        </article>
      </section>

      {loading ? <p className="intel-meta">Loading source quality…</p> : null}
      {error ? <p className="intel-error">{error}</p> : null}

      {!loading && !error ? (
        <div className="source-quality-table-wrap">
          <table className="source-quality-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Trust</th>
                <th>Label</th>
                <th>Confidence</th>
                <th>Runs</th>
                <th>Fresh</th>
                <th>Complete</th>
                <th>Images</th>
                <th>Duplicates</th>
                <th>Stale</th>
                <th>Review</th>
                <th>Errors</th>
                <th>Calculated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((source) => (
                <tr key={source.sourceKey}>
                  <td>
                    <strong>{source.displayName}</strong>
                    <span className="muted">{source.sourceKey}</span>
                    {source.topIssues?.length ? (
                      <span className="source-quality-table__issue">{source.topIssues[0]}</span>
                    ) : null}
                  </td>
                  <td>
                    <StatusBadge
                      label={source.trustScore ?? "—"}
                      tone={trustScoreTone(source.trustScore)}
                    />
                  </td>
                  <td>{source.trustLabel || "Unknown"}</td>
                  <td>
                    <StatusBadge
                      label={source.trustConfidence || "UNKNOWN"}
                      tone={confidenceTone(source.trustConfidence)}
                    />
                  </td>
                  <td>{source.metrics?.runReliabilityScore ?? "—"}</td>
                  <td>{source.metrics?.freshnessScore ?? "—"}</td>
                  <td>{source.metrics?.dataCompletenessScore ?? "—"}</td>
                  <td>
                    {source.metrics?.imageCoverageScore ?? "—"}
                    {source.summary?.healthyImagesCount != null ||
                    source.summary?.brokenImagesCount != null ? (
                      <span className="muted source-quality-table__image-meta">
                        {source.summary?.healthyImagesCount ?? 0} ok ·{" "}
                        {source.summary?.brokenImagesCount ?? 0} broken ·{" "}
                        {source.summary?.uncheckedImagesCount ?? 0} unchecked
                      </span>
                    ) : null}
                  </td>
                  <td>{source.metrics?.duplicateMatchQualityScore ?? "—"}</td>
                  <td>{source.metrics?.staleRateScore ?? "—"}</td>
                  <td>{source.metrics?.reviewBurdenScore ?? "—"}</td>
                  <td>{source.metrics?.errorSeverityScore ?? "—"}</td>
                  <td>
                    {source.trustLastCalculatedAt
                      ? new Date(source.trustLastCalculatedAt).toLocaleString()
                      : "—"}
                  </td>
                  <td className="source-quality-table__actions">
                    <button type="button" onClick={() => openDetail(source.sourceKey)}>
                      Details
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRecalculateOne(source.sourceKey)}
                      disabled={recalculating}
                    >
                      Recalc
                    </button>
                    {onOpenSourceControls ? (
                      <button type="button" onClick={() => onOpenSourceControls(source.sourceKey)}>
                        Controls
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {selectedKey ? (
        <SourceQualityDetailDrawer
          detail={detail}
          loading={detailLoading}
          onClose={() => {
            setSelectedKey(null);
            setDetail(null);
          }}
          onRecalculate={() => handleRecalculateOne(selectedKey)}
          recalculating={recalculating}
        />
      ) : null}
    </div>
  );
};

export default SourceQualityPage;
